import { ClientEntity, OccupationType } from 'api/types/entity';
import { UpdateClientParams } from 'api/types/params';
import { isFinanceProject, isGlobalProject } from 'config';
import { yesOrNot } from 'helpers/yesOrNo';
import {
  parseAdditionalInformationFileIdsParam,
  parseTaxResidencyParam,
  parsePepConnectionParam,
  parseBiographyParam,
  parseCitizenshipsParam,
  parseServiceContractParam,
  parseAdditionalDocumentFileIdsParam,
  parseAddressWithFilesParam,
  parseDocumentsFileIdsParam,
  parseW8BENFormParam,
  parseIdentityDocumentWithFilesParam,
} from 'modules/client/common/helpers';

import { ClientFormType } from '../../forms/clientForm';

import { getBankAccountsParam } from './getBankAccountsParam';
import { getBusinessRelationshipParam } from './getBusinessRelationshipParam';
import { getDebtsParam } from './getDebtsParam';
import { getFinancialPositionParam } from './getFinancialPositionParam';
import { getFinancingSourcesParam } from './getFinancingSourcesParam';
import { getKnowledgeAndExperienceParam } from './getKnowledgeAndExperienceParam';
import { getSourcesOfIncomeParam } from './getSourcesOfIncomeParam';
import { getWithdrawalAccountsParam } from './getWithdrawalAccountsParam';

export const getClientSubmitData = (
  client: ClientEntity,
  values: ClientFormType
): UpdateClientParams => {
  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();
  const { placeOfBirth, registrationAddress, residentialAddress } =
    parseAddressWithFilesParam(values);

  const {
    Invoice,
    SignaturesCard,
    InvoicePaymentConfirmation,
    DocumentGroups,
    CompanySignedNoticeOfInvestmentAccountOpening,
    CompanySignedAgreement_Finance,
    SignedApplicationStandardAgreement_Finance,
    SignedOpenSubAccountOrder_Finance,
  } = parseDocumentsFileIdsParam(values.documentsFiles);

  const { Selfie } = parseDocumentsFileIdsParam(values.identityDocument.files);

  return {
    versionNumber: client.versionNumber!,
    reason: values.reasonChange.reason,
    firstName: values.personalData.firstName,
    lastName: values.personalData.lastName,
    middleName: values.personalData.middleName ?? null,
    dateOfBirth: values.personalData.dateOfBirth,
    gender: values.personalData.gender?.value,
    // not for global project
    bankAccounts: isFinance ? getBankAccountsParam(values) : undefined,
    // not for global project
    biography: isFinance ? parseBiographyParam(values) : undefined,
    citizenships: parseCitizenshipsParam(values),
    // not for global project
    debts: isFinance
      ? getDebtsParam(
          values,
          values.biography?.occupation?.value === OccupationType.entrepreneur
        )
      : undefined,
    // not for global project
    financingSources: isFinance ? getFinancingSourcesParam(values) : undefined,

    financialPosition: isGlobal ? getFinancialPositionParam(values) : undefined,

    sourcesOfIncome: isGlobal ? getSourcesOfIncomeParam(values) : undefined,

    orderSubmissionContacts: values.orderSubmissionContacts.map(
      ({ type, value }) => ({
        value,
        type: type.value,
      })
    ),
    // not for global project
    registrationAddress: isFinance ? registrationAddress : undefined,
    residentialAddress,
    placeOfBirth,

    taxResidences: parseTaxResidencyParam(values, true),

    withdrawalAccounts: getWithdrawalAccountsParam(values),
    pepConnection: values.pepConnection.relation?.value!,
    // not for global project
    connectedPepInfo: isFinance ? parsePepConnectionParam(values) : undefined,
    accreditedInvestorStatus:
      yesOrNot(values.statuses.accreditedInvestorStatus?.value) ?? null,

    riskLevel: values.riskLevel.riskLevel?.value ?? null,
    informationUpdateFrequency:
      values.riskLevel.informationUpdateFrequency?.value ?? null,
    riskLevelReviewDate: values.riskLevel.riskLevelReviewDate ?? null,
    lastInformationUpdateDate:
      values.riskLevel.lastInformationUpdateDate ?? null,
    lastOperationMonitoringResults:
      values.riskLevel.lastOperationMonitoringResults ?? null,
    usedServices: values.riskLevel.usedServices ?? null,

    code: values.accountCodes.code ?? null,
    centralDepositoryAccountNumber:
      values.accountCodes.centralDepositoryAccountNumber ?? null,
    centralDepositoryAccountNumberOpeningDate:
      values.accountCodes.centralDepositoryAccountNumberOpeningDate ?? null,
    personalAccountNumber: values.accountCodes.personalAccountNumber,
    personalAccountOpeningDate: values.accountCodes.personalAccountOpeningDate,
    managerId: values.accountCodes.manager?.id ?? null,
    personalAccountClosingDate:
      values.accountCodes.personalAccountClosingDate ?? null,
    personalAccountFreezingDate:
      values.accountCodes.personalAccountFreezingDate ?? null,

    additionalInformationFileIds: parseAdditionalInformationFileIdsParam(
      values.additionalDocumentsFiles
    ),

    additionalDocumentFileIds: parseAdditionalDocumentFileIdsParam(
      values.documentsFiles
    ),

    openAccountOrder: isFinance
      ? {
          date: values.openAccountOrder.date || null,
          number: values.openAccountOrder.number || null,
          orderReceivedDate: values.openAccountOrder.orderReceivedDate || null,
          fileId: undefined,
        }
      : undefined,

    category: values.general.category?.value || null,
    economicSector: values.general.economicSector?.value || null,
    classification: values.general.classification?.value || null,
    clientType: values.general.clientType?.value || null,
    taxBenefits: values.general.taxBenefits || null,
    managementType: values.general.managementType?.value || null,
    isFinancialMonitoringSubject:
      yesOrNot(values.general.isFinancialMonitoringSubject?.value) ?? null,
    isKazakhstanResident:
      yesOrNot(values.general.isKazakhstanResident?.value) ?? null,
    isInsider: yesOrNot(values.general.isInsider?.value) ?? null,

    serviceContract: parseServiceContractParam(values),

    identityDocument: parseIdentityDocumentWithFilesParam(values),

    tariff: values.general.tariff?.value,

    businessRelationship: isGlobal
      ? getBusinessRelationshipParam(values)
      : undefined,

    riskProfile: isGlobal ? values.riskProfile?.value : undefined,
    W8BENForm: isGlobal ? parseW8BENFormParam(values) : undefined,

    knowledgeAndExperience: isGlobal
      ? getKnowledgeAndExperienceParam(values)
      : undefined,

    documentGroupsFileIds: DocumentGroups?.map((f) => f.id!),

    invoice: client.invoice
      ? {
          fileId: Invoice?.[0]?.id,
          paymentConfirmationFileIds: InvoicePaymentConfirmation?.map(
            (v) => v.id!
          ),
        }
      : undefined,

    selfieFileIds: Selfie?.map((f) => f.id!),
    signaturesCardFileIds: SignaturesCard?.map((f) => f.id!),
    companySignedNoticeOfInvestmentAccountOpeningFileId:
      CompanySignedNoticeOfInvestmentAccountOpening?.[0]?.id || null,

    companySignedAgreementFileId_Finance:
      CompanySignedAgreement_Finance?.[0]?.id || null,

    signedApplicationStandardAgreementFileId_Finance:
      SignedApplicationStandardAgreement_Finance?.[0]?.id || null,

    signedOpenSubAccountOrderFileId_Finance:
      SignedOpenSubAccountOrder_Finance?.[0]?.id || null,
  };
};
